import React, { useState, useEffect, useCallback, useRef } from "react";
import Content from "../../../layout/content/Content";
import Head from "../../../layout/head/Head";
import {
  Block,
  BlockBetween,
  BlockDes,
  BlockHead,
  BlockHeadContent,
  BlockTitle,
  Button,
  Icon,
  PreviewCard,
} from "../../../components/Component";
import { useNavigate } from "react-router-dom";
import FormInput from "../../form-components/InputComponent";
import FormSelect from "../../form-components/SelectComponent";
import FormDatePicker from "../../form-components/DatePicker";
import { useForm, FormProvider } from "react-hook-form";
import { Col, Row, Alert } from "reactstrap";
import { Select, Table, InputNumber, Tag, Tooltip } from "antd";
import LoadingSpinner from "../../../components/loading/LoadingSpinner";
import Swal from "sweetalert2";
import superAdminService from "../../../api/super-admin/superAdminService";
import misc from "../../../api/misc/miscService";
import type {
  Currency,
  Country,
  State,
  AppDropdownItem,
} from "../../../types/super-admin";

const pricingTypeOptions = [
  { value: "Flat", label: "Flat" },
  { value: "User-based", label: "User-based" },
];

const billingFrequencyOptions = [{ value: "Monthly", label: "Monthly" }];

interface SelectedApp {
  appId: number;
  appName: string;
  displayPrice: number | null;
  sortOrder: number;
}

const BundleCreate: React.FC = () => {
  const navigate = useNavigate();
  const formMethods = useForm<Record<string, any>>({
    mode: "onChange",
    defaultValues: {
      pricingType: "Flat",
      billingFrequency: "Monthly",
    },
  });
  const { handleSubmit, watch } = formMethods;

  const [loading, setLoading] = useState(false);
  const [currencies, setCurrencies] = useState<{ value: any; label: string }[]>([]);
  const [currencyList, setCurrencyList] = useState<Currency[]>([]);
  const [countries, setCountries] = useState<{ value: any; label: string }[]>([]);
  const [states, setStates] = useState<{ value: any; label: string }[]>([]);
  const [appOptions, setAppOptions] = useState<{ value: number; label: string; disabled?: boolean }[]>([]);
  const [selectedApps, setSelectedApps] = useState<SelectedApp[]>([]);
  const [alertInfo, setAlertInfo] = useState({
    type: "",
    message: "",
    errors: [] as string[],
  });

  const selectedCountry = watch("countryId");
  const selectedState = watch("stateId");
  const selectedPricingType = watch("pricingType");
  const selectedCurrency = watch("currencyId");
  const selectedValidFrom = watch("validFrom");

  const currencySymbol = (() => {
    if (!selectedCurrency) return "";
    const found = currencyList.find((c) => c.id === Number(selectedCurrency));
    return found?.currency_symbol || "";
  })();

  // Active bundle duplicate check (blocks when same app combination exists)
  const [activeBundleWarning, setActiveBundleWarning] = useState("");
  const [duplicateBlocked, setDuplicateBlocked] = useState(false);

  const fetchDropdowns = useCallback(async () => {
    const results = await Promise.allSettled([
      superAdminService.getAppDropdown(),
      misc.getCurrencies(),
      misc.getCountries(),
    ]);

    if (results[0].status === "fulfilled") {
      const appRes = results[0].value;
      if (appRes.status && appRes.data) {
        const apps: AppDropdownItem[] = Array.isArray(appRes.data) ? appRes.data : [];
        setAppOptions(
          apps.map((app) => ({
            value: app.appId,
            label: app.appCode
              ? `${app.appCode} - ${app.appName} (${app.moduleCount || 0} modules)`
              : `${app.appName} (${app.moduleCount || 0} modules)`,
            disabled: !app.moduleCount,
          }))
        );
      }
    }

    if (results[1].status === "fulfilled") {
      const currenciesRes = results[1].value;
      if (currenciesRes.status && currenciesRes.data) {
        const list: Currency[] = Array.isArray(currenciesRes.data) ? currenciesRes.data : [];
        setCurrencyList(list);
        setCurrencies(
          list.map((item) => ({
            value: item.id,
            label: `${item.currency_code} - ${item.description || item.currency_code}`,
          }))
        );
      }
    }

    if (results[2].status === "fulfilled") {
      const countriesRes = results[2].value;
      if (countriesRes.status && countriesRes.data) {
        const list: Country[] = Array.isArray(countriesRes.data) ? countriesRes.data : [];
        setCountries(
          list.map((item) => ({ value: item.countryId, label: item.countryName }))
        );
      }
    }
  }, []);

  useEffect(() => {
    fetchDropdowns();
  }, []);

  useEffect(() => {
    if (selectedCountry) {
      const fetchStates = async () => {
        try {
          const statesRes = await misc.getStatesByCountry(Number(selectedCountry));
          if (statesRes.status && statesRes.data) {
            const list: State[] = Array.isArray(statesRes.data) ? statesRes.data : [];
            setStates(list.map((item) => ({ value: item.stateId, label: item.stateName })));
          }
        } catch (error: any) {
          console.error("Failed to fetch states:", error.message);
        }
      };
      fetchStates();
    } else {
      setStates([]);
    }
  }, [selectedCountry]);

  // Check for active bundle with same app combination when location/apps change
  const bundleCheckRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const selectedAppIds = selectedApps.map((a) => a.appId);

  useEffect(() => {
    if (bundleCheckRef.current) clearTimeout(bundleCheckRef.current);

    // Need at least 2 apps to check
    if (selectedAppIds.length < 2) {
      setActiveBundleWarning("");
      setDuplicateBlocked(false);
      return;
    }

    bundleCheckRef.current = setTimeout(async () => {
      try {
        const res = await superAdminService.checkActiveBundle({
          countryId: selectedCountry ? Number(selectedCountry) : null,
          stateId: selectedState ? Number(selectedState) : null,
          pricingType: selectedPricingType || null,
          appIds: selectedAppIds,
        });
        if (res.status && res.data?.exists) {
          const d = res.data;
          const location = d.countryName
            ? `${d.countryName}${d.stateName ? ` — ${d.stateName}` : ""}`
            : "Global";
          const validUntil = d.validTo
            ? new Date(d.validTo).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })
            : "N/A";

          const hierarchyNotes: Record<string, string> = {
            country_level_exists: " (country-level bundle covers this state)",
            state_level_exists: " (state-level bundles exist under this country)",
            global_level_exists: " (global-level bundle covers all locations)",
            lower_level_exists: " (country/state-level bundles exist)",
          };
          const hierarchyNote = hierarchyNotes[d.conflictType] || "";
          const pricingInfo = d.pricingType ? ` [${d.pricingType}]` : "";
          setActiveBundleWarning(
            `An active bundle "${d.bundleName}" (${d.bundleCode})${pricingInfo} with the same app combination already exists for ${location}${hierarchyNote}, valid until ${validUntil}. Please modify the existing bundle or choose a different pricing type/apps.`
          );
          setDuplicateBlocked(true);
        } else {
          setActiveBundleWarning("");
          setDuplicateBlocked(false);
        }
      } catch {
        setActiveBundleWarning("");
        setDuplicateBlocked(false);
      }
    }, 500);

    return () => {
      if (bundleCheckRef.current) clearTimeout(bundleCheckRef.current);
    };
  }, [selectedCountry, selectedState, selectedPricingType, selectedAppIds.join(",")]);

  const handleAppSelect = (appIds: number[]) => {
    const updated: SelectedApp[] = appIds.map((id, idx) => {
      const existing = selectedApps.find((a) => a.appId === id);
      if (existing) return existing;
      const opt = appOptions.find((o) => o.value === id);
      return {
        appId: id,
        appName: opt?.label || `App ${id}`,
        displayPrice: null,
        sortOrder: idx + 1,
      };
    });
    setSelectedApps(updated);
  };

  const updateAppField = (appId: number, field: string, value: any) => {
    setSelectedApps((prev) =>
      prev.map((a) => (a.appId === appId ? { ...a, [field]: value } : a))
    );
  };

  const removeApp = (appId: number) => {
    setSelectedApps((prev) => prev.filter((a) => a.appId !== appId));
  };

  const displayPriceSum = selectedApps.reduce((sum, a) => sum + (a.displayPrice || 0), 0);

  const appColumns = [
    {
      title: "#",
      key: "index",
      width: 50,
      align: "center" as const,
      render: (_: any, __: any, idx: number) => (
        <span className="text-muted">{idx + 1}</span>
      ),
    },
    {
      title: "Application",
      dataIndex: "appName",
      key: "appName",
      render: (text: string) => <strong>{text}</strong>,
    },
    {
      title: "Display Price",
      dataIndex: "displayPrice",
      key: "displayPrice",
      width: 160,
      render: (_: any, record: SelectedApp) => (
        <InputNumber
          size="small"
          min={0}
          precision={2}
          placeholder="0.00"
          value={record.displayPrice}
          onChange={(val) => updateAppField(record.appId, "displayPrice", val)}
          style={{ width: "100%" }}
          addonBefore={currencySymbol || "—"}
        />
      ),
    },
    {
      title: "Sort Order",
      dataIndex: "sortOrder",
      key: "sortOrder",
      width: 100,
      align: "center" as const,
      render: (_: any, record: SelectedApp) => (
        <InputNumber
          size="small"
          min={1}
          value={record.sortOrder}
          onChange={(val) => updateAppField(record.appId, "sortOrder", val || 1)}
          style={{ width: 70 }}
        />
      ),
    },
    {
      title: "",
      key: "action",
      width: 50,
      align: "center" as const,
      render: (_: any, record: SelectedApp) => (
        <Tooltip title="Remove app">
          <a
            href="#remove"
            onClick={(e) => {
              e.preventDefault();
              removeApp(record.appId);
            }}
            className="text-danger"
            style={{ fontSize: 16 }}
          >
            <Icon name="cross-circle" />
          </a>
        </Tooltip>
      ),
    },
  ];

  const submitForm = async (data: any) => {
    if (duplicateBlocked) {
      Swal.fire(
        "Restricted",
        "An active bundle with the same app combination already exists for this location.",
        "warning"
      );
      return;
    }

    if (selectedApps.length < 2) {
      setAlertInfo({
        type: "error",
        message: "Please select at least 2 apps for the bundle.",
        errors: [],
      });
      return;
    }

    // Validate display price sum vs bundle price
    const appsWithPrice = selectedApps.filter((a) => a.displayPrice != null);
    if (appsWithPrice.length > 0 && data.bundlePrice) {
      const displayPriceSum = appsWithPrice.reduce((sum, a) => sum + (a.displayPrice || 0), 0);
      const bundlePrice = Number(data.bundlePrice);
      if (Math.abs(displayPriceSum - bundlePrice) > 0.01) {
        setAlertInfo({
          type: "error",
          message: `Sum of display prices (${displayPriceSum.toFixed(2)}) does not match the bundle price (${bundlePrice.toFixed(2)}). Please adjust display prices or bundle price.`,
          errors: [],
        });
        return;
      }
    }

    setLoading(true);
    setAlertInfo({ type: "", message: "", errors: [] });

    try {
      const payload: any = {
        bundleCode: data.bundleCode?.trim(),
        bundleName: data.bundleName?.trim(),
        apps: selectedApps.map((a) => ({
          appId: a.appId,
          ...(a.displayPrice != null && { displayPrice: a.displayPrice }),
          sortOrder: a.sortOrder,
        })),
        currencyId: Number(data.currencyId),
        pricingType: data.pricingType,
        bundlePrice: Number(data.bundlePrice),
        billingFrequency: data.billingFrequency || "Monthly",
        validFrom: data.validFrom
          ? new Date(data.validFrom).toLocaleDateString("en-CA")
          : undefined,
        validTo: data.validTo
          ? new Date(data.validTo).toLocaleDateString("en-CA")
          : undefined,
      };

      if (data.description) payload.description = data.description.trim();
      if (data.countryId) payload.countryId = Number(data.countryId);
      if (data.stateId) payload.stateId = Number(data.stateId);

      const res = await superAdminService.createBundle(payload);
      if (res.status) {
        Swal.fire("Created!", "Bundle has been created successfully.", "success");
        navigate("/sa-bundles");
      } else {
        const errorMsg = Array.isArray(res.message)
          ? res.message.join(", ")
          : res.message || "An error occurred";
        setAlertInfo({ type: "error", message: errorMsg, errors: [] });
      }
    } catch (error: any) {
      setAlertInfo({
        type: "error",
        message: error.message || "An error occurred",
        errors: [],
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <React.Fragment>
      <Head title="Create Enterprise Pricing" />
      <Content>
        <BlockHead size="sm">
          <BlockBetween>
            <BlockHeadContent>
              <BlockTitle tag="h3" page>
                Create Bundle
              </BlockTitle>
              <BlockDes className="text-soft">
                <p>Create a new bundle configuration</p>
              </BlockDes>
            </BlockHeadContent>
            <BlockHeadContent>
              <Button
                color="primary"
                className="btn-dim"
                onClick={() => navigate("/sa-bundles")}
              >
                <Icon name="menu-alt-r"></Icon>
                <span>Bundle List</span>
              </Button>
            </BlockHeadContent>
          </BlockBetween>
        </BlockHead>

        <Block>
          <PreviewCard className="cardBox" bodyClass="card-inner-xl">
            {alertInfo.message && (
              <div className="mb-3">
                <Alert
                  color={alertInfo.type === "error" ? "danger" : "success"}
                  className="alert-icon alert-animated"
                >
                  <strong>{alertInfo.message}</strong>
                  <Icon
                    name={
                      alertInfo.type === "error"
                        ? "alert-circle"
                        : "check-circle"
                    }
                  />
                </Alert>
              </div>
            )}
            {activeBundleWarning && (
              <div className="mb-3">
                <Alert color="warning" className="alert-icon alert-animated">
                  <Icon name="alert-fill" />
                  <strong>{activeBundleWarning}</strong>
                </Alert>
              </div>
            )}
            <FormProvider {...formMethods}>
              <form
                className="content clearfix"
                onSubmit={handleSubmit(submitForm)}
              >
                {/* Basic Information */}
                <h6 className="overline-title text-primary mb-3">Basic Information</h6>
                <Row className="gy-3">
                  <Col lg="4">
                    <FormInput
                      name="bundleCode"
                      label="Bundle Code"
                      type="text"
                      required={true}
                      maxLength={50}
                      placeholder="e.g. BND-001"
                    />
                  </Col>
                  <Col lg="4">
                    <FormInput
                      name="bundleName"
                      label="Bundle Name"
                      type="text"
                      required={true}
                      maxLength={200}
                      placeholder="Enter bundle name"
                    />
                  </Col>
                  <Col lg="4">
                    <FormInput
                      name="description"
                      label="Description"
                      type="text"
                      maxLength={500}
                      placeholder="Optional description"
                    />
                  </Col>
                </Row>

                <hr className="my-4" />

                {/* Applications */}
                <h6 className="overline-title text-primary mb-3">Applications</h6>
                <Row className="gy-3">
                  <Col lg="12">
                    <div className="form-group">
                      <label className="form-label">
                        Select Applications <span className="text-danger">*</span>{" "}
                        <small className="text-muted">(min 2)</small>
                      </label>
                      <Select
                        mode="multiple"
                        placeholder="Search and select applications"
                        style={{ width: "100%" }}
                        showSearch
                        optionFilterProp="label"
                        value={selectedApps.map((a) => a.appId)}
                        onChange={handleAppSelect}
                        options={appOptions}
                      />
                      {selectedApps.length > 0 && (
                        <div className="mt-3">
                          <div className="d-flex justify-content-between align-items-center mb-2">
                            <Tag color="blue">{selectedApps.length} app(s) selected</Tag>
                            {displayPriceSum > 0 && (
                              <Tag color="green">Display Price Total: {currencySymbol}{displayPriceSum.toFixed(2)}</Tag>
                            )}
                          </div>
                          <Table
                            columns={appColumns}
                            dataSource={selectedApps}
                            rowKey="appId"
                            pagination={false}
                            bordered
                            size="middle"
                            className="bundle-apps-table"
                            summary={() =>
                              selectedApps.some((a) => a.displayPrice != null) ? (
                                <Table.Summary fixed>
                                  <Table.Summary.Row>
                                    <Table.Summary.Cell index={0} colSpan={2} align="right">
                                      <strong>Total Display Price</strong>
                                    </Table.Summary.Cell>
                                    <Table.Summary.Cell index={1} align="center">
                                      <strong>{currencySymbol}{displayPriceSum.toFixed(2)}</strong>
                                    </Table.Summary.Cell>
                                    <Table.Summary.Cell index={2} />
                                    <Table.Summary.Cell index={3} />
                                  </Table.Summary.Row>
                                </Table.Summary>
                              ) : null
                            }
                          />
                        </div>
                      )}
                    </div>
                  </Col>
                </Row>

                <hr className="my-4" />

                {/* Pricing & Billing */}
                <h6 className="overline-title text-primary mb-3">Pricing & Billing</h6>
                <Row className="gy-3">
                  <Col lg="3">
                    <FormSelect
                      name="currencyId"
                      label="Currency"
                      options={currencies}
                      required={true}
                      placeholder="-Select-"
                      showSearch={true}
                    />
                  </Col>
                  <Col lg="3">
                    <FormSelect
                      name="pricingType"
                      label="Pricing Type"
                      options={pricingTypeOptions}
                      required={true}
                      placeholder="-Select-"
                    />
                  </Col>
                  <Col lg="3">
                    <FormInput
                      name="bundlePrice"
                      label="Bundle Price"
                      required={true}
                      type="number"
                      placeholder="Enter price"
                    />
                  </Col>
                  <Col lg="3">
                    <FormSelect
                      name="billingFrequency"
                      label="Billing Frequency"
                      options={billingFrequencyOptions}
                      required={true}
                      placeholder="-Select-"
                    />
                  </Col>
                </Row>

                <hr className="my-4" />

                {/* Location */}
                <h6 className="overline-title text-primary mb-3">Location</h6>
                <Row className="gy-3">
                  <Col lg="4">
                    <FormSelect
                      name="countryId"
                      label="Country"
                      options={countries}
                      placeholder="Global if empty"
                      showSearch={true}
                    />
                  </Col>
                  {selectedCountry && (
                    <Col lg="4">
                      <FormSelect
                        name="stateId"
                        label="State"
                        options={states}
                        placeholder="-Select State-"
                        showSearch={true}
                      />
                    </Col>
                  )}
                </Row>

                <hr className="my-4" />

                {/* Validity Period */}
                <h6 className="overline-title text-primary mb-3">Validity Period</h6>
                <Row className="gy-3">
                  <Col lg="4">
                    <FormDatePicker
                      name="validFrom"
                      label="Valid From"
                      required={true}
                      placeholder="Start date"
                    />
                  </Col>
                  <Col lg="4">
                    <FormDatePicker
                      name="validTo"
                      label="Valid To"
                      required={true}
                      placeholder="End date"
                      disabled={!selectedValidFrom}
                      minDate={selectedValidFrom ? new Date(selectedValidFrom) : undefined}
                    />
                  </Col>
                </Row>

                <Row className="mt-4">
                  <Col lg="12">
                    <ul className="align-center flex-wrap flex-sm-nowrap gx-4 gy-2">
                      <li>
                        <Button color="primary" size="md" type="submit" disabled={duplicateBlocked}>
                          {loading ? <LoadingSpinner size="sm" inline /> : "Create Bundle"}
                        </Button>
                      </li>
                      <li>
                        <a
                          href="#cancel"
                          onClick={(e) => {
                            e.preventDefault();
                            if (!loading) navigate("/sa-bundles");
                          }}
                          className={`link link-light${loading ? " disabled text-muted" : ""}`}
                          style={loading ? { pointerEvents: "none" } : {}}
                        >
                          Cancel
                        </a>
                      </li>
                    </ul>
                  </Col>
                </Row>
              </form>
            </FormProvider>
          </PreviewCard>
        </Block>
      </Content>
    </React.Fragment>
  );
};

export default BundleCreate;
